import { configurePreferences, hydratePreferences } from '@orbit-mind/engine-react';
import { applyAppearance, readAppearance } from './account/appearance';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

import { createRouter } from './routes';
import { registerServiceWorker } from './pwa';
import { count, watchForTheEnd } from './telemetry/telemetry';
import { UpdateBanner } from './ui/UpdateBanner';
// Self-hosted rather than linked from a CDN: a PWA whose shell works offline cannot depend on a
// font server it has no way to reach. The variable file is one request for every weight the
// interface uses.
import '@fontsource-variable/inter';
import './index.css';
import { getLocale, setLocale, subscribeLocale } from '@orbit-mind/engine-react';
try {
  const stored = localStorage.getItem('orbit-mind.locale');
  if (stored === 'en' || stored === 'pt-BR') setLocale(stored);
} catch {
  /* Storage may be restricted. */
}
configurePreferences((value) => {
  localStorage.setItem('orbit-mind.preferences', value);
  return Promise.resolve();
});
try {
  hydratePreferences(localStorage.getItem('orbit-mind.preferences'));
} catch {
  /* Device storage is optional for preferences. */
}
applyAppearance(readAppearance());
document.documentElement.lang = getLocale();
subscribeLocale(() => {
  document.documentElement.lang = getLocale();
  try {
    localStorage.setItem('orbit-mind.locale', getLocale());
  } catch {
    /* The current session still changes language. */
  }
});

const container = document.getElementById('root');
if (container === null) {
  // A missing mount point means the shell HTML is wrong; failing loudly beats a blank page.
  throw new Error('Orbit Mind: #root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <RouterProvider router={createRouter()} />
    {/*
      Outside the router on purpose: an update is about the *application*, not about whichever
      screen happens to be open, and a banner mounted inside a route would vanish on navigation.
    */}
    <UpdateBanner />
  </StrictMode>,
);

/*
 * The splash in `index.html` goes once the application has painted: two frames, so the first one
 * the application draws is on screen before the splash starts to fade, and the page never shows
 * the gap between them.
 */
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    const splash = document.getElementById('splash');
    if (splash === null) return;
    splash.classList.add('splash-gone');
    splash.addEventListener('transitionend', () => {
      splash.remove();
    });
    // A browser that skips the transition (reduced motion, a background tab) still loses it.
    setTimeout(() => {
      splash.remove();
    }, 600);
  });
});

// After the render, so the first paint is never waiting on a registration.
registerServiceWorker();

/*
 * The funnel's denominator, and the listener that gets the rest of it out.
 *
 * Both do nothing at all without consent — `count` asks every time, and a flush with an empty
 * batch is a no-op — so this costs a browser that declined exactly one event listener.
 */
count({ kind: 'funnel', event: 'app-opened' });
watchForTheEnd();
